// Application state store using Zustand
// Voice-first architecture - voice state is primary

import { create } from 'zustand';
import type {
  InterfaceMode,
  VoiceState,
  VoiceIntent,
  ImageMetadata,
  Vehicle,
  ComparisonPair,
  SearchFilters,
} from '@/types';

interface AppStore {
  // Interface mode - controlled by voice or touch
  mode: InterfaceMode;
  setMode: (mode: InterfaceMode) => void;

  // Voice state - primary interaction driver
  voiceState: VoiceState;
  setVoiceState: (state: VoiceState) => void;
  lastIntent: VoiceIntent | null;
  setLastIntent: (intent: VoiceIntent | null) => void;
  transcript: string;
  setTranscript: (transcript: string) => void;

  // Current vehicle context
  currentVehicle: Vehicle | null;
  setCurrentVehicle: (vehicle: Vehicle | null) => void;

  // Search state
  searchQuery: string;
  setSearchQuery: (query: string) => void;
  searchFilters: SearchFilters;
  setSearchFilters: (filters: SearchFilters) => void;
  searchResults: ImageMetadata[];
  setSearchResults: (results: ImageMetadata[]) => void;
  highlightedTags: string[];
  setHighlightedTags: (tags: string[]) => void;

  // Image viewing
  selectedImage: ImageMetadata | null;
  selectImage: (image: ImageMetadata | null) => void;

  // Comparison mode
  comparisonPair: ComparisonPair | null;
  setComparisonPair: (pair: ComparisonPair | null) => void;

  // Showcase mode
  showcaseImages: ImageMetadata[];
  setShowcaseImages: (images: ImageMetadata[]) => void;

  // Loading/Error states
  isLoading: boolean;
  setIsLoading: (loading: boolean) => void;
  error: string | null;
  setError: (error: string | null) => void;

  // Reset to initial state
  reset: () => void;
}

const initialState = {
  mode: 'search' as InterfaceMode,
  voiceState: 'idle' as VoiceState,
  lastIntent: null,
  transcript: '',
  currentVehicle: null,
  searchQuery: '',
  searchFilters: {},
  searchResults: [],
  highlightedTags: [],
  selectedImage: null,
  comparisonPair: null,
  showcaseImages: [],
  isLoading: false,
  error: null,
};

export const useAppStore = create<AppStore>((set) => ({
  ...initialState,

  setMode: (mode) => set({ mode }),

  setVoiceState: (voiceState) => set({ voiceState }),
  setLastIntent: (lastIntent) => set({ lastIntent }),
  setTranscript: (transcript) => set({ transcript }),

  setCurrentVehicle: (currentVehicle) => set({ currentVehicle }),

  setSearchQuery: (searchQuery) => set({ searchQuery }),
  setSearchFilters: (searchFilters) => set({ searchFilters }),
  setSearchResults: (searchResults) => set({ searchResults }),
  setHighlightedTags: (highlightedTags) => set({ highlightedTags }),

  selectImage: (selectedImage) =>
    set({
      selectedImage,
      mode: selectedImage ? 'detail' : 'search',
    }),

  setComparisonPair: (comparisonPair) =>
    set({
      comparisonPair,
      mode: comparisonPair ? 'comparison' : 'search',
    }),

  setShowcaseImages: (showcaseImages) => set({ showcaseImages }),

  setIsLoading: (isLoading) => set({ isLoading }),
  setError: (error) => set({ error }),

  reset: () => set(initialState),
}));
